
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';

// Define our analytics event names as constants
export const ANALYTICS_EVENTS = {
  // Assessment events
  ASSESSMENT_STARTED: 'assessment_started',
  ASSESSMENT_COMPLETED: 'assessment_completed',
  ASSESSMENT_SECTION_COMPLETED: 'assessment_section_completed',
  SCORE_ATTEMPTED: 'score_attempted',
  INDUSTRY_SELECTED: 'industry_selected',
  
  // Engagement events
  FEATURE_INTEREST: 'feature_interest',
  AUDIENCE_ENGAGEMENT: 'audience_engagement',
  
  // Artifact events
  ARTIFACT_CREATED: 'artifact_created',
  ARTIFACT_UPDATED: 'artifact_updated',
  ARTIFACT_VIEWED: 'artifact_viewed',
  
  // Career Progress events
  LEVEL_UP: 'level_up',
  SKILL_UPDATED: 'skill_updated',
  MILESTONE_ACHIEVED: 'milestone_achieved',
  
  // Guidance events
  GUIDANCE_VIEWED: 'guidance_viewed',
  ACTION_COMPLETED: 'action_completed',
  
  // User events
  PROFILE_UPDATED: 'profile_updated',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  
  // Feature usage
  FEATURE_USED: 'feature_used',
  ERROR_OCCURRED: 'error_occurred',
  DASHBOARD_ERROR: 'dashboard_error',
  
  // Navigation events
  PAGE_VIEW: 'pageview',
  NAVIGATION_CLICKED: 'navigation_clicked',
  
  // Chat specific events
  CHAT_CLEARED: 'chat_cleared',
  MESSAGE_SENT: 'message_sent',
  ARTIFACT_SUGGESTED: 'artifact_suggested',
  AGENT_SWITCHED: 'agent_switched',
  
  // Analytics events
  ANALYTICS_VIEWED: 'admin_analytics_viewed',
  ANALYTICS_FILTERED: 'admin_analytics_filtered',
  ANALYTICS_EXPORTED: 'admin_analytics_exported',
  ANALYTICS_DATA_REQUESTED: 'admin_analytics_data_requested',
  ANALYTICS_TIME_RANGE_CHANGED: 'admin_analytics_time_range_changed'
} as const;

export type AnalyticsEventName = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];

interface AnalyticsProps {
  [key: string]: string | number | boolean | undefined;
}

export const usePostHogAnalytics = () => {
  const location = useLocation();

  // Track page views
  useEffect(() => {
    if (posthog.__loaded) {
      posthog.capture('$pageview');
    }
  }, [location]);

  // Track custom events with properties
  const trackEvent = (eventName: AnalyticsEventName, props?: AnalyticsProps) => {
    if (posthog.__loaded) {
      console.log('Tracking PostHog event:', eventName, props);
      posthog.capture(eventName, props);
    }
  };

  // Track feature usage
  const trackFeatureUsage = (featureName: string, action: string, additionalProps?: AnalyticsProps) => {
    trackEvent(ANALYTICS_EVENTS.FEATURE_USED, {
      feature: featureName,
      action,
      ...additionalProps
    });
  };

  // Track errors
  const trackError = (errorType: string, errorMessage: string, additionalProps?: AnalyticsProps) => {
    trackEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
      type: errorType,
      message: errorMessage,
      ...additionalProps
    });
  };

  // Track navigation
  const trackNavigation = (destination: string, source?: string) => {
    trackEvent(ANALYTICS_EVENTS.NAVIGATION_CLICKED, {
      destination,
      source: source || location.pathname
    });
  };

  // Identify user (call this when user logs in)
  const identifyUser = (userId: string, userProps?: Record<string, any>) => {
    if (posthog.__loaded) {
      posthog.identify(userId, userProps);
    }
  };

  return {
    trackEvent,
    trackFeatureUsage,
    trackError,
    trackNavigation,
    identifyUser
  };
};
